import { BridgeBase, CalibrationEllipse, CalibrationMode, CaseModel, CaseModelToolsData, Consts, LayersBase, MainBase, MeasuresController, MechanicalAxisAP, PrintUtilsBase, SaveHandlerBase, SceneBase, Tools, ToolsInitBase, ViewType } from '@ortho-next/nextray-core';
import { DirectionalLight } from 'three';
import { getAppConfig } from '../../app/core';
import { AppModel } from './AppModel';
import { BindedModel, bindedModel } from './BindedModel';
import { Bridge } from './Bridge';
import { Context } from './Context';

class ToolsAP extends Tools { //lateral is not present, so reuse AP
	viewType = ViewType.AP;
	mechanicalAxis!: MechanicalAxisAP;
	contralateralMechanicalAxis!: MechanicalAxisAP;
};

export class Main extends MainBase {
	public declare model: AppModel;
	public declare bridge: Bridge;
	protected declare _context: Context;
	protected override _isPatientGarage = true;
	protected _toolsDataModel?: CaseModelToolsData;

	constructor(canvasContainer: HTMLDivElement) {
		super(canvasContainer, {
			appModel: AppModel,
			bindedModel: new BindedModel(),
			bridge: Bridge as typeof BridgeBase,
			contextProto: Context,
			measuresController: MeasuresController,
			printUtilsProto: PrintUtilsBase,
			saveHandlerProto: SaveHandlerBase,
			sceneProto: SceneBase,
			toolsAPProto: ToolsAP,
			toolsLTProto: ToolsAP
		}, true);

		Consts.cdnUrl = getAppConfig().cdnUrl;
		this.setView(ViewType.AP);

		const dirLight = new DirectionalLight(0x555555, 0.7);
		dirLight.position.set(0, 0, 500);
		this.activeScene.add(dirLight);

		this._context.setModels(this.model, this.toolsAP);

		this._context.addEventListener('calibrate', (event) => {
			const calibrationTool = this.activeTools.calibrationTool!;
			if (calibrationTool.mode === CalibrationMode.ellipse) {
				const ellipse = calibrationTool.tool as CalibrationEllipse;
				this.model.calibrationEllipsePoints = [ellipse.coneA.position, ellipse.coneB.position, ellipse.coneC.position, ellipse.rotationPoint.position];
				ellipse.ring.updateMatrix();
				this.model.ringMatrix = ellipse.ring.matrix;
				this.model.angle = ellipse.ring.angle;
				this.model.frontal = ellipse.ring.frontal;
				this.model.axial = ellipse.ring.axial;
			}
		});

		this._context.addEventListener('setRingCalibrationData', (event) => {
			const calibrationTool = this.activeTools.calibrationTool!;
			if (calibrationTool.mode === CalibrationMode.ellipse) {
				const ellipse = calibrationTool.tool as CalibrationEllipse;
				ellipse.setRingCalibrationData(event.args);
			}
		});

		this._context.addEventListener('setRing', (event) => {
			const calibrationTool = this.activeTools.calibrationTool!;
			if (calibrationTool.mode === CalibrationMode.ellipse) {
				const ellipse = calibrationTool.tool as CalibrationEllipse;
				ellipse.setRing(event.args?.ringModel, event.args?.oppositeRingType);
			}
		});

		this._context.addEventListener("resetImageTools", async () => {
			await this.resetToolsData();
		});
	}

	public override async loadScenes(caseModel: CaseModel): Promise<void> {
		if (this.activeTools.plane) {
			await this.resetModel(caseModel);
		} else {
			await super.loadScenes(caseModel, ToolsInitBase, LayersBase);
		}

		this._context.updateDataToSave();
	}

	private resetModel(model: CaseModel): Promise<void> {
		return new Promise<void>((resolve) => {
			this.caseModel = model;
			this.updateBrightnessContrastFromModel(model); // we need to update brightness and contrast in a sync way to avoid problem with angular binding

			this.resetScene();

			this.activeTools.plane!.loadImage(model.imgAP!, async () => { // TODO await load image callback?
				this.applyToolsData(model);
				await this.applyInitialFlipGarage();
				this.activeCameraControl.fitToPlane();
				resolve();
			});
		});
	}

	protected override applyToolsData(caseModel: CaseModelToolsData, updateToolsDataModel = true): void {
		super.applyToolsData(caseModel);

		if (!updateToolsDataModel) return;

		this._toolsDataModel = {
			brightnessAP: caseModel.brightnessAP,
			brightnessLT: 0,
			contrastAP: caseModel.contrastAP,
			contrastLT: 0,
			planeRotationAP: caseModel.planeRotationAP ?? 0,
			flipTypeAP: caseModel.flipTypeAP,
		}
	}

	private resetScene(): void {
		this.activeTools.plane!.reset();
		bindedModel.calibratingAP = false;
		bindedModel.scaleFactorAP = 1;

		(this.model as any).scaleFactorAP = undefined;
		(this.model as any).brightnessAP = undefined;
		(this.model as any).contrastAP = undefined;
		this.model.planeRotation = undefined;
		this.model.calibrationEllipsePoints = undefined;
		this.model.ringMatrix = undefined
		this.model.angle = undefined
		this.model.frontal = undefined;
		this.model.axial = undefined;
	}

	protected async resetToolsData(): Promise<void> {
		if (this._toolsDataModel) {
			this.updateBrightnessContrastFromModel(this._toolsDataModel);
			this.applyToolsData(this._toolsDataModel, false);
			this.resetGarageFlip();
			await this.applyInitialFlipGarage();
			this.model.planeRotation = this._toolsDataModel.planeRotationAP;
			return;
		}

		this.toolsAP?.plane?.reset();
		this.toolsLT?.plane?.reset();

		this.model.brightnessAP = 0;
		this.model.contrastAP = 0;
		this.model.brightnessLT = 0;
		this.model.contrastLT = 0;
		this.model.planeRotation = 0;
	}

	protected resetGarageFlip(): void {
		const plane = this.toolsAP.plane!;
		plane.resetGarageFlip();
	}
}
